import { Component, DestroyRef, inject, ChangeDetectionStrategy, signal, computed } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterOutlet } from '@angular/router';
import MenuBrowserComponent from './menu-browser/menu-browser.component';
import MenuOutletComponent from './menu-outlet/menu-outlet.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationService } from '../../location.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { PaintDirective } from '../../paint.directive';
import { ResizeService } from '../../auth.service';
import { MatError, MatHint } from '@angular/material/form-field';
import { debounceTime } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'menus',
  templateUrl: './menus.component.html',
  styleUrls: ['./menus.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, PaintDirective, MatHint, MatError, RouterOutlet, ReactiveFormsModule, RouterLink, NgTemplateOutlet],
  host: {
    '[class.no-footer]': '!isMenuView'
  },
  animations: [
    trigger('step', [
      state('true', style({
          height: '*'
      }), { params : { height: 325 } }),
      state('false', style({
          height: '{{height}}px'
      }), { params : { height: 225 } }),
      transition('true <=> false', [
          animate("250ms cubic-bezier(0.455, 0.03, 0.515, 0.955)")
      ]),
    ])
  ],
})
export default class MenusComponent  {
  destroy = inject(DestroyRef);
  activated = inject(ActivatedRoute);
  router = inject(LocationService);
  size = inject(ResizeService);

  search = new FormControl('', { nonNullable: true });
  placeholder = 'Search for restaurants or cities';

  qrcode = new FormControl('', {
     validators: [Validators.required, Validators.pattern(/^[0-9]{8}$/)],
     nonNullable: true
  });

  child_route = signal<MenuBrowserComponent | MenuOutletComponent | null>(null);
  qr_view = signal<boolean>(false);
  dismissed = signal<boolean>(false);
  
  menu_source = computed(() => {
    const child_route = this.child_route();

    return child_route instanceof MenuOutletComponent ? child_route.type() : 'none';
  });

  menu_selected = computed(() => {
    const child_route = this.child_route();
    
    return child_route instanceof MenuOutletComponent ? child_route.menu()?.selected_menu() ?? null : null;
  });

  qr_instructions = computed(() => {
    const child_route = this.child_route();
    const width = this.size.width();

    return (!child_route || child_route instanceof MenuBrowserComponent) && !this.dismissed() && !this.search.value && width <= 800;
  });

  constructor() {
    if(this.activated.snapshot.queryParams){
        if(this.activated.snapshot.queryParams.s){
          this.router.navigate(['/menus/store', this.activated.snapshot.queryParams.s]);
        } else if(this.activated.snapshot.queryParams.q){
          this.search.setValue(this.activated.snapshot.queryParams.q);
        }
    }

    this.qrcode.valueChanges.pipe(takeUntilDestroyed()).subscribe(val => {
      if(!val) return; 
 
      let numeric = val.replace(/[^0-9]/gm, '');
 
      if(val != numeric){
        this.qrcode.setValue(numeric);
      } else if(val.length == 8 && this.qrcode.valid){
        this.onQRCode();
      }
    });
 
    this.search.valueChanges.pipe(takeUntilDestroyed(), debounceTime(700)).subscribe(query => this.onSearch());

   }

  onQRCode(){
    if(this.qrcode.valid && this.qrcode.value && this.qrcode.value.length == 8){
      this.router.navigate(['/menus/store', 'MT' + this.qrcode.value]);
      setTimeout(() => {
        const child_route = this.child_route();
        if(child_route instanceof MenuOutletComponent){
          this.qrcode.reset('', { emitEvent: false });//if we stayed on the outlet component properly
          this.qr_view.set(false);
        }
      }, 2500);
    } else {
      this.qrcode.markAllAsTouched();
    }
  }

  onSearch(){
    const child_route = this.child_route();
    if(child_route instanceof MenuBrowserComponent) {
      child_route.search.set(this.search.value.trim());
    } else {
      this.router.navigate(['/menus']).then(() => this.onSearch());
    }
  }

  onRoute(component: MenuBrowserComponent | MenuOutletComponent){
      this.child_route.set(component);
      if(component instanceof MenuBrowserComponent){
        if(this.search && this.search.value)
          this.onSearch();

        if(this.qr_view() && this.qrcode.valid && this.qrcode.value && this.qrcode.value.length == 8){
          requestAnimationFrame(() => {
            this.qrcode.setErrors({ badCode: true });
            this.qrcode.markAsTouched();
          })
        }
      }

  }
}
